/**
 * Generates stable, type-safe class names and CSS selectors for styled components.
 * Replaces Emotion component selectors which break after publishing.
 *
 * @returns `cls` — class name strings for `className` props in JSX
 * @returns `sel` — CSS selectors (prefixed with `.`) for use in style definitions
 *
 * @example
 * const components = { Button, Icon };
 * const { cls, sel } = createClassNames('Button', components);
 *
 * // JSX: <S.Icon className={S.cls.Icon} />
 * // Styles: [S.sel.Icon]: { color: 'red' }
 */
export const createClassNames = <const T extends Record<string, unknown>>(component: string, subComponents: T) => {
  const entries = Object.keys(subComponents).map((key) => [
    key,
    `sf-${component}-${key.charAt(0).toLowerCase() + key.slice(1)}`,
  ]);

  return {
    /** Class name strings for `className` props in JSX */
    cls: Object.fromEntries(entries) as { readonly [K in keyof T & string]: string },
    /** CSS selectors (`.sf-...`) for use in style definitions */
    sel: Object.fromEntries(entries.map(([k, v]) => [k, `.${v}`])) as {
      readonly [K in keyof T & string]: string;
    },
  };
};
